import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLeaf,
  faCheck,
  faUndo,
  faSpinner,
  faPlus,
  faTrash,
  faExclamationTriangle,
  faCalendarPlus,
} from "@fortawesome/free-solid-svg-icons";
import { Typography } from "@mui/material";
import config from "../../config";
import { useDataCache, CACHE_KEYS } from "../../contexts/DataCacheContext";
import LastUpdatedBadge from "../ui/LastUpdatedBadge";
import BaseDialog from "../ui/BaseDialog";
import ActionButton from "../ui/ActionButton";

// validation constraints
const MIN_GOAL = 1; // must be at least 1 kg
const MAX_GOAL = 10000000; // 10 million kg
const DEFAULT_NEW_YEAR_GOAL = 20000;

const CURRENT_YEAR = new Date().getFullYear();

// format "last saved" timestamp
const formatLastSaved = (timestamp) => {
  if (!timestamp) return null;

  const date = new Date(timestamp);

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

function GHGGoalsSection({ darkMode, t }) {
  const [goals, setGoals] = useState({});
  const [originalGoals, setOriginalGoals] = useState({}); // track original for reset
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [goalsUpdatedAt, setGoalsUpdatedAt] = useState(null); // when goals were last saved by user
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [yearToRemove, setYearToRemove] = useState(null);
  const [yearToAdd, setYearToAdd] = useState(null);

  const { getCachedData, setCachedData } = useDataCache();

  const isFetching = useRef(false);
  const isInitialLoad = useRef(true);

  // compute isDirty by comparing current goals to original
  const isDirty = useMemo(() => {
    const currentKeys = Object.keys(goals).sort();
    const originalKeys = Object.keys(originalGoals).sort();

    // different number of years
    if (currentKeys.length !== originalKeys.length) return true;

    // different years or values
    for (const key of currentKeys) {
      if (!originalGoals.hasOwnProperty(key)) return true;
      if (goals[key] !== originalGoals[key]) return true;
    }

    return false;
  }, [goals, originalGoals]);

  // get list of years that can be added (range: current year - 5 to current year + 5, excluding existing)
  const availableYearsToAdd = useMemo(() => {
    const existingYears = Object.keys(goals).map(Number);
    const years = [];
    for (let y = CURRENT_YEAR - 5; y <= CURRENT_YEAR + 5; y++) {
      if (!existingYears.includes(y)) {
        years.push(y);
      }
    }
    return years;
  }, [goals]);

  // check if a year can be removed (can't remove if only one year left)
  const canRemoveYear = useCallback(
    (year) => {
      // Can't remove if only one year left
      if (Object.keys(goals).length <= 1) return false;
      return true;
    },
    [goals],
  );

  // fetch goals + progress from API
  const fetchData = useCallback(async () => {
    if (isFetching.current) return;
    isFetching.current = true;

    if (!isInitialLoad.current) {
      setRefreshing(true);
    }

    try {
      let userGoals = {};
      let updatedAt = null;
      try {
        const goalsRes = await fetch(`${config.apiUrl}/user/ghg-goals`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
        if (goalsRes.ok) {
          const goalsData = await goalsRes.json();
          if (goalsData.success && goalsData.goals) {
            userGoals = { ...goalsData.goals };
            updatedAt = goalsData.updated_at || null; // timestamp from backend
          }
        }
      } catch (err) {
        console.warn("Failed to fetch user goals:", err);
      }

      setGoals(userGoals);
      setOriginalGoals(userGoals); // store original for reset
      setGoalsUpdatedAt(updatedAt);

      // fetch progress for all years in PARALLEL
      const years = Object.keys(userGoals);
      const progressPromises = years.map(async (year) => {
        try {
          const startDate = `${year}-01-01`;
          const endDate = `${year}-12-31`;
          const params = new URLSearchParams({
            date_from: startDate,
            date_to: endDate,
          });

          if (config.isDemoMode) {
            params.append("show_all", "true");
          }

          const res = await fetch(
            `${config.apiUrl}/donor/ghg/summary?${params}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
              },
            },
          );

          if (res.ok) {
            const data = await res.json();
            if (data.success) {
              const achieved = data.totalAvoided || 0;
              const target = userGoals[year] || 0;
              return {
                year,
                achieved,
                pct: target > 0 ? Math.round((achieved / target) * 100) : 0,
              };
            }
          }
        } catch (err) {
          console.error(`Failed to fetch GHG for ${year}:`, err);
        }
        return { year, achieved: 0, pct: 0 };
      });

      const results = await Promise.all(progressPromises);

      const progressData = {};
      results.forEach(({ year, achieved, pct }) => {
        progressData[year] = { achieved, pct };
      });

      setProgress(progressData);

      const timestamp = Date.now();
      setLastUpdated(timestamp);
      setCachedData(
        CACHE_KEYS.PROFILE_GHG_GOALS,
        {
          goals: userGoals,
          progress: progressData,
          goalsUpdatedAt: updatedAt,
        },
        timestamp,
      );
    } catch (err) {
      console.error("Error fetching GHG goals data:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
      isFetching.current = false;
      isInitialLoad.current = false;
    }
  }, [setCachedData]);

  // initial load - check cache first
  useEffect(() => {
    const contextCache = getCachedData(CACHE_KEYS.PROFILE_GHG_GOALS);
    if (contextCache && contextCache.data) {
      const cachedGoals = contextCache.data.goals || {};
      setGoals(cachedGoals);
      setOriginalGoals(cachedGoals);
      setProgress(contextCache.data.progress || {});
      setGoalsUpdatedAt(contextCache.data.goalsUpdatedAt || null);
      setLastUpdated(contextCache.timestamp);
      setLoading(false);
      isInitialLoad.current = false;
      return;
    }

    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(() => {
    setRefreshing(true);
    fetchData();
  }, [fetchData]);

  const updateGoal = (year, val) => {
    const parsed = parseInt(val, 10) || 0;
    const clamped = Math.max(MIN_GOAL, Math.min(MAX_GOAL, parsed));
    setGoals((prev) => ({ ...prev, [year]: clamped }));
  };

  // reset to original values (what was last saved)
  const reset = () => {
    setGoals({ ...originalGoals });
  };

  // add year handlers
  const handleAddYearClick = () => {
    // default to current year if available, otherwise first available
    const defaultYear = availableYearsToAdd.includes(CURRENT_YEAR)
      ? CURRENT_YEAR
      : availableYearsToAdd[0];
    setYearToAdd(defaultYear);
    setShowAddModal(true);
  };

  const confirmAddYear = () => {
    setGoals((prev) => ({
      ...prev,
      [yearToAdd]: DEFAULT_NEW_YEAR_GOAL,
    }));
    setShowAddModal(false);
    setYearToAdd(null);
  };

  // remove year handlers
  const handleRemoveYearClick = (year) => {
    setYearToRemove(year);
    setShowRemoveModal(true);
  };

  const confirmRemoveYear = () => {
    setGoals((prev) => {
      const newGoals = { ...prev };
      delete newGoals[yearToRemove];
      return newGoals;
    });
    setShowRemoveModal(false);
    setYearToRemove(null);
  };

  const save = async () => {
    const invalidGoals = Object.entries(goals).filter(
      ([, val]) => val < MIN_GOAL || val > MAX_GOAL,
    );
    if (invalidGoals.length > 0) {
      setAlert({
        type: "error",
        msg: t("Goals must be between 1 and 10,000,000 kg"),
      });
      setTimeout(() => setAlert(null), 4000);
      return;
    }

    setSaving(true);
    setAlert(null);

    try {
      const res = await fetch(`${config.apiUrl}/user/ghg-goals`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: JSON.stringify({ goals }),
      });

      if (res.ok) {
        const result = await res.json();
        const newUpdatedAt = result.updated_at || new Date().toISOString();

        setOriginalGoals({ ...goals }); // update original after save
        setGoalsUpdatedAt(newUpdatedAt);
        setAlert({ type: "success", msg: t("Goals saved") });

        const timestamp = Date.now();
        setLastUpdated(timestamp);
        setCachedData(
          CACHE_KEYS.PROFILE_GHG_GOALS,
          {
            goals,
            progress,
            goalsUpdatedAt: newUpdatedAt,
          },
          timestamp,
        );
      } else {
        setAlert({ type: "error", msg: t("Failed to save goals") });
      }
    } catch {
      setAlert({ type: "error", msg: t("Failed to save goals") });
    }

    setSaving(false);
    setTimeout(() => setAlert(null), 3000);
  };

  const fmt = (n) => n?.toLocaleString() ?? "0";

  const progressText = (year) => {
    if (loading || refreshing) {
      return (
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          className="profile-loading-icon"
        />
      );
    }

    const p = progress[year];
    if (!p || p.achieved === 0) return t("Not started");

    const yearNum = parseInt(year, 10);
    const verb = yearNum < CURRENT_YEAR ? t("achieved") : t("so far");
    return (
      <>
        <strong>{fmt(p.achieved)} kg</strong> {verb} ({p.pct}%)
      </>
    );
  };

  // sort years for display
  const sortedYears = useMemo(() => {
    return Object.keys(goals).sort((a, b) => parseInt(a) - parseInt(b));
  }, [goals]);

  if (loading) {
    return (
      <div className="profile-section">
        <div className="profile-section-header">
          <div className="profile-section-icon">
            <FontAwesomeIcon icon={faLeaf} />
          </div>
          <div>
            <h2 className="profile-section-title">
              {t("Annual GHG Reduction Targets")}
            </h2>
            <p className="profile-section-desc">
              {t("Set your yearly goals for emissions avoided")}
            </p>
          </div>
        </div>
        <div
          style={{ display: "flex", justifyContent: "center", padding: "40px" }}
        >
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            size="2x"
            style={{ color: "var(--text-secondary)" }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="profile-section">
      <div className="profile-section-header">
        <div className="profile-section-icon">
          <FontAwesomeIcon icon={faLeaf} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 className="profile-section-title">
            {t("Annual GHG Reduction Targets")}
          </h2>
          <p className="profile-section-desc">
            {t("Set your yearly goals for emissions avoided")}
            <span className="profile-goals-last-saved">
              {" · "}
              {goalsUpdatedAt
                ? `${t("Set on")} ${formatLastSaved(goalsUpdatedAt)}`
                : t("Not yet set")}
            </span>
          </p>
        </div>
        <LastUpdatedBadge
          timestamp={lastUpdated}
          onRefresh={refresh}
          isRefreshing={refreshing}
        />
      </div>

      {alert && (
        <div className={`profile-alert ${alert.type}`}>
          <FontAwesomeIcon
            icon={alert.type === "success" ? faCheck : faExclamationTriangle}
          />
          {alert.msg}
        </div>
      )}

      <div
        className={`profile-goals-grid ${refreshing ? "is-refreshing" : ""}`}
      >
        {sortedYears.map((year) => {
          const target = goals[year];
          const isCurrent = parseInt(year, 10) === CURRENT_YEAR;
          const p = progress[year] || { pct: 0 };
          const removable = canRemoveYear(year);

          return (
            <div
              key={year}
              className={`profile-goal-card ${isCurrent ? "current" : ""}`}
            >
              <div className="profile-goal-year">
                <span>{year}</span>
                <div className="profile-goal-year-actions">
                  {isCurrent && (
                    <span className="profile-goal-badge">{t("Current")}</span>
                  )}
                  {removable && (
                    <button
                      className="profile-goal-remove-btn"
                      onClick={() => handleRemoveYearClick(year)}
                      title={t("Remove year")}
                      disabled={refreshing}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
              </div>
              <div className="profile-goal-input-wrap">
                <input
                  type="number"
                  className="profile-goal-input"
                  value={target}
                  min={MIN_GOAL}
                  max={MAX_GOAL}
                  step="100"
                  onChange={(e) => updateGoal(year, e.target.value)}
                  disabled={refreshing}
                />
                <span className="profile-goal-unit">kg CO₂e</span>
              </div>
              <div className="profile-goal-progress">
                <div className="profile-progress-bar">
                  <div
                    className="profile-progress-fill"
                    style={{ width: `${Math.min(p.pct || 0, 100)}%` }}
                  />
                </div>
                <div className="profile-progress-text">
                  {progressText(year)}
                </div>
              </div>
            </div>
          );
        })}

        {/* Add Year Card */}
        {availableYearsToAdd.length > 0 && (
          <button
            className="profile-goal-card profile-goal-add-card"
            onClick={handleAddYearClick}
            disabled={refreshing}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>{t("Add Year")}</span>
          </button>
        )}
      </div>

      {/* Only show buttons when there are changes */}
      {isDirty && (
        <div className="profile-btn-row">
          <button
            className="profile-btn profile-btn-secondary"
            onClick={reset}
            disabled={refreshing}
          >
            <FontAwesomeIcon icon={faUndo} />
            {t("Reset")}
          </button>
          <button
            className="profile-btn profile-btn-primary"
            onClick={save}
            disabled={saving || refreshing}
          >
            <FontAwesomeIcon icon={faCheck} />
            {saving ? t("Saving...") : t("Save Goals")}
          </button>
        </div>
      )}

      {/* Add Year Confirmation Modal */}
      <BaseDialog
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={t("Add Year")}
        darkMode={darkMode}
        icon={{ icon: faCalendarPlus, variant: "info" }}
        actions={
          <>
            <ActionButton
              onClick={() => setShowAddModal(false)}
              variant="default"
              modal
            >
              {t("Cancel")}
            </ActionButton>
            <ActionButton
              onClick={confirmAddYear}
              variant="default"
              modal
              disabled={!yearToAdd}
            >
              {t("Add Year")}
            </ActionButton>
          </>
        }
      >
        <div style={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontSize: "0.95rem",
              color: darkMode ? "#e0e0e0" : "#666666",
              marginBottom: "16px",
            }}
          >
            {t("Select a year to add a GHG reduction goal:")}
          </Typography>
          <select
            value={yearToAdd || ""}
            onChange={(e) => setYearToAdd(Number(e.target.value))}
            className="profile-year-select"
            style={{
              padding: "10px 20px",
              fontSize: "1rem",
              fontFamily: "Poppins",
              fontWeight: 500,
              borderRadius: "8px",
              border: darkMode
                ? "1px solid rgba(102, 157, 46, 0.4)"
                : "1px solid rgba(102, 157, 46, 0.3)",
              backgroundColor: darkMode
                ? "rgba(102, 157, 46, 0.1)"
                : "rgba(102, 157, 46, 0.05)",
              color: darkMode ? "#8bc34a" : "#558b2f",
              cursor: "pointer",
              minWidth: "120px",
              outline: "none",
            }}
          >
            {availableYearsToAdd.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </BaseDialog>

      {/* Remove Year Confirmation Modal */}
      <BaseDialog
        open={showRemoveModal}
        onClose={() => setShowRemoveModal(false)}
        title={t("Remove Year")}
        darkMode={darkMode}
        icon={{ icon: faTrash, variant: "danger" }}
        actions={
          <>
            <ActionButton
              onClick={() => setShowRemoveModal(false)}
              variant="default"
              modal
            >
              {t("Cancel")}
            </ActionButton>
            <ActionButton onClick={confirmRemoveYear} variant="danger" modal>
              {t("Remove")}
            </ActionButton>
          </>
        }
      >
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: "1rem",
            color: darkMode ? "#e0e0e0" : "#666666",
            textAlign: "center",
          }}
        >
          {t(
            "Are you sure you want to remove the goal for {{year}}? This won't delete any recorded emissions data.",
            { year: yearToRemove },
          )}
        </Typography>
      </BaseDialog>
    </div>
  );
}

export default GHGGoalsSection;
